import { useEffect, useRef } from "react";
import { useAuth } from "../auth/useAuth";
import type { User } from "../repositories/auth.repository";
import { useNotificationPreferencesQuery } from "../hooks/useNotificationPreferencesQuery";
import { usePushNotifications } from "../hooks/usePushNotifications";

const ENROLLMENT_ANALYTICS = {
  pageKey: "app",
  routePath: "/",
  sourcePage: "App",
  productArea: "notifications",
} as const;

/**
 * Silently registers push when the user already granted browser permission
 * and their notification preference has push enabled (default: on).
 *
 * Re-posts an existing browser PushSubscription to the API so account switches
 * rebind the endpoint to the signed-in user.
 */
export const PushNotificationEnrollment = () => {
  const { isAuthenticated, user } = useAuth();
  const userId = (user as User | null)?.id ?? null;
  const { data: preferences, isSuccess } = useNotificationPreferencesQuery({
    enabled: isAuthenticated,
  });
  const { ensureSubscribed, permission, refreshSubscriptionState } =
    usePushNotifications();
  const syncedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      syncedUserIdRef.current = null;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !isSuccess || !userId) {
      return;
    }

    if (syncedUserIdRef.current === userId) {
      return;
    }

    if (preferences?.push !== true) {
      return;
    }

    if (permission !== "granted") {
      return;
    }

    syncedUserIdRef.current = userId;

    void ensureSubscribed(ENROLLMENT_ANALYTICS).then((subscribed) => {
      if (!subscribed) {
        syncedUserIdRef.current = null;
        return;
      }

      void refreshSubscriptionState();
    });
  }, [
    ensureSubscribed,
    isAuthenticated,
    isSuccess,
    permission,
    preferences?.push,
    refreshSubscriptionState,
    userId,
  ]);

  return null;
};
